import { createLinkEntry } from '@lib/utils/create-link-entry'

import { getConfig } from '@config/index'

import { IconSprites } from '@constants/enums/sprites'

import { trustedByLogos } from '@custom/shared/const'

import type { HomepageData } from './types'

const config = getConfig()

export const homepageContent: HomepageData = {
  banner: {
    content: {
      id: 'banner',
      copy: 'Looking for Superhuman Mail?',
      link: createLinkEntry(`${config.paths.mail.landing}`, {
        title: 'Learn more',
        internalName: 'Learn More CTA',
      }),
    },
  },
  heroSection: {
    content: {
      id: 'hero-section',
      title: 'Superpowers, everywhere you work',
      heading: 'Mail, Docs, and AI that works in every app and tab',
      link: createLinkEntry(`${config.api.signup}?screen_hint=signup`, {
        title: 'Get Superhuman',
        internalName: 'Get Superhuman CTA',
      }),
    },
  },
  trustedBySection: {
    heading: 'Trusted by the most innovative companies in the world',
    logos: trustedByLogos,
  },
  tabbedSection: {
    heading: 'Your Superhuman suite',
    link: createLinkEntry(`${config.paths.basePath}${config.paths.plans}`, {
      title: 'Get the suite',
      internalName: 'Get the suite CTA',
    }),
    content: [
      {
        icon: IconSprites.SuperhumanMailWithBg,
        id: 'tabbed-section-superhuman-mail',
        title: 'Mail',
        heading: 'The most productive email app ever made',
        copy: 'Fly through your inbox twice as fast as before, never drop the ball again, and save 4 hours every single week.',
        list: [
          'Respond faster to what matters most',
          'Follow up on time, every time',
          'Write with AI that sounds like you',
          'Save 4 hours every single week',
        ],
        link: createLinkEntry(config.paths.mail.landing, {
          title: 'Learn more about Mail',
          internalName: 'Learn more about Mail CTA',
        }),
        media: {
          video: {
            sources: ['/homepage/Homepage_Mail_Animation_1336x1358_101025_optimized.mp4'],
            height: 1336,
            width: 1358,
            poster:
              '//images.ctfassets.net/vwfkeyj6n9ac/6GZdcneKJhk1BOMc4wSVFP/76ff2c85fd93f8171adc15fae2f7df56/mail-fallback.png',
          },
        },
      },
      {
        id: 'tabbed-section-grammarly',
        icon: IconSprites.GrammarlyBubble,
        title: 'Grammarly',
        heading: 'Everyone’s favorite AI writing partner',
        copy: 'Turn your thoughts into writing that’s clear, credible, and impossible to ignore.',
        list: [
          'Works everywhere you write',
          'Find the right words instantly',
          'Write with AI that adapts to your tone and voice',
          'Let your brilliance shine',
        ],
        link: createLinkEntry(`https://${config.domains.main}/products/grammarly`, {
          title: 'Learn more about Grammarly',
          internalName: 'Learn more about Grammarly CTA',
        }),
        media: {
          video: {
            sources: ['/homepage/Homepage_Grammarly_Animation_1336x1358_101025_optimized.mp4'],
            height: 1336,
            width: 1358,
            poster:
              '//images.ctfassets.net/vwfkeyj6n9ac/4Xbsd7oCDAyYCJROHZ80Oo/e8f5b1d2fab4760b806f6413cd256f89/grammarly-fallback.png',
          },
        },
      },
      {
        id: 'tabbed-section-coda',
        icon: IconSprites.CodaLogo,
        title: 'Coda',
        heading: 'The all-in-one AI workspace for teams',
        copy: 'Build everything from wikis, through project plans, to goal trackers — keeping everyone perfectly in sync.',
        list: [
          'Connect Slack, Jira, Salesforce, and 800+ tools',
          'Build your team wiki, project plans, and company goal trackers',
          'Create a single source of truth for all your team’s knowledge',
          'Save time on manual tasks with Coda AI',
        ],
        link: createLinkEntry(`https://${config.domains.main}/products/coda`, {
          title: 'Learn more about Coda',
          internalName: 'Learn more about Coda CTA',
        }),
        media: {
          video: {
            sources: ['/homepage/Homepage_Coda_Animation_1336x1358_101025_optimized.mp4'],
            height: 1336,
            width: 1358,
            poster:
              '//images.ctfassets.net/vwfkeyj6n9ac/aX0n8e13NwFiOnXVCAG8u/eed3b77003f84aa38d0bb4eaf5becc6f/coda-fallback.png',
          },
        },
      },
      {
        id: 'tabbed-section-go',
        icon: IconSprites.GoLogo,
        title: 'Go',
        heading: 'AI that actually works in every app you use',
        copy: 'Go is the proactive AI assistant that knows what you know and offers help without you having to ask.',
        list: [
          'Connect Gmail, Drive, Jira, and all of your favorite apps',
          'Say the right thing with the right info, everywhere you write',
          'Schedule meetings without leaving the conversation',
          'Work with any app, from any app',
        ],
        link: createLinkEntry(`https://${config.domains.main}/products/go-ai-assistant`, {
          title: 'Learn more about Go',
          internalName: 'Learn more about Go CTA',
        }),
        media: {
          video: {
            sources: ['/homepage/Homepage_Go_Animation_1336x1358_102325_optimized.mp4'],
            height: 1334,
            width: 1358,
            poster:
              '//images.ctfassets.net/vwfkeyj6n9ac/2Elaw7EDLXcTtRhFmYlD3c/048b30c77421faf80ce7556320cd98c6/go-fallback.png',
          },
        },
      },
    ],
  },
  manifestoSection: {
    content: {
      id: 'manifesto-section',
      heading: 'Becoming Superhuman.',
      copy: 'When AI works everywhere you work, it starts to change <em>how</em> you work. At first, you think faster and more deeply. Before you know it, you have the time to be more creative, strategic, and impactful—free to do what only you can do.',
      link: createLinkEntry(`https://blog.superhuman.com/introducing-new-superhuman/`, {
        title: 'Read our announcement',
        internalName: 'Read our announcement CTA',
      }),
      media: {
        image: {
          path: '//images.ctfassets.net/vwfkeyj6n9ac/6dRKs0BhqWbnOdnl43ifjt/0a2f56e8ac1c8cddfe72634f7983b440/homepage-manifesto.webp',
          alt: 'Girl drafting a Superhuman manifesto',
          height: 1158,
          width: 1408,
        },
      },
    },
  },
  superhumanSection: {
    content: {
      id: 'superhuman-section',
      heading: 'AI that works everywhere you work',
      link: createLinkEntry(`${config.api.signup}?screen_hint=signup`, {
        title: 'Get Superhuman',
        internalName: 'Get Superhuman CTA',
      }),
      media: {
        image: {
          path: '//images.ctfassets.net/vwfkeyj6n9ac/1MZDaABlJ6y5NbSOUUeaTN/0e6ab0d1c98158fb9b6c6a6c2e089734/homepage-tonal-flower.webp',
          alt: 'Tonal Flower',
          height: 3024,
          width: 960,
        },
      },
    },
  },
}
